<script>
  import { _ } from 'svelte-i18n'
  import { modals, flash_content, user_storefronts, detected_fonts, dropped_font_files, font_upload_progress, upload_storefront_modal_mode, max_upload_file_size } from '../../stores'
  import { flatMap, filter, find } from 'lodash'
  import Fieldset from '../../elements/Fieldset.svelte'
  import Button from '../../elements/Button.svelte'
  import FormError from '../../elements/FormError.svelte'
  import Modal from '../../elements/Modal.svelte'
  import ButtonGroup from '../../elements/ButtonGroup.svelte'
  import SelectList from '../../elements/SelectList.svelte'
  import { saveStorefrontData } from '../../services/storefronts'
  import { uploadAllFonts, sanitizeFilename, formatFileSize, validateFileSize } from '../../services/font_file_upload'
  import { formatFontList } from '../../modules/helpers'
  import Label from '../../elements/Label.svelte'
  import IconCloudDone from '../../elements/IconCloudDone.svelte'
  import Blink from '../../elements/Blink.svelte';
  
  // Import functional utilities
  import { validateFontSizes, prepareFontEntries } from '../../services/font_file_upload_size_validation'
  import { getModalTitle, getIntroText, getButtonText, getErrorMessage, generateSuccessMessage } from '../../services/upload_modal'

  let button_busy = false
  let form_element
  let show_save_error = false
  let storefront_name = ''
  let list_items = []
  let selected_fonts = []
  let oversized_fonts = [] // Track fonts that are too large
  let valid_fonts_for_upload = [] // Track fonts that can be uploaded
  let fonts_already_uploaded = []
  let font_list_element
  let validating_fonts = false // Track font validation state

  // Get current mode from the store
  $: currentMode = $upload_storefront_modal_mode

  // Compute button text, modal title, etc. using utility functions
  $: buttonText = getButtonText(currentMode, $font_upload_progress, valid_fonts_for_upload.length)
  $: modalTitle = getModalTitle(currentMode)
  $: introText = getIntroText(currentMode, selected_fonts.length, oversized_fonts.length)
  $: errorMessage = getErrorMessage(currentMode)

  $: $user_storefronts, setListItems()

  function setListItems() {
    list_items = flatMap($user_storefronts, (storefront) => storefront.name)
  }

  $: selected_fonts = flatMap(filter($detected_fonts, 'selected'), (f) => ({
    postscript_name: f.postscript_name,
    uploaded: f.uploaded || false, // uploaded fonts have the uploaded property set to true
    file_name: f.file_name, // dropped fonts have the file_name set
    display_name: f.display_name
  }))

  // Get font counts for UI display (computed from selected_fonts)
  $: fontsToUpload = selected_fonts.filter(font => !font.uploaded)

  // Get fonts that can be saved to storefront (only uploaded fonts with file_name after they are uploaded)
  $: fonts_valid_for_storefront = selected_fonts.filter(font => font.uploaded && font.file_name)

  // Get detected fonts that are validated for upload (reactive to changes in $detected_fonts)
  $: detected_fonts_validated_to_upload = valid_fonts_for_upload.map(validFont => {
    const detectedFont = $detected_fonts.find(f => f.postscript_name === validFont.postscript_name)
    return { ...detectedFont }
  })

  // Scroll to the last uploaded element when detected_fonts_validated_to_upload changes
  $: detected_fonts_validated_to_upload, scrollToLastUploadedElement()

  // Detect when modal is visible and validate font sizes
  $: modalVisible = $modals.find(m => m.id === 'uploadOrAddToStorefront')?.visible || false
  $: if (modalVisible) {
    validateFontSizesForModal()
  }

  function scrollToLastUploadedElement() {
    if (font_list_element) {
      // Get all uploaded elements and select the last one
      const uploadedElements = font_list_element.querySelectorAll('li.uploaded')
      const lastUploadedElement = uploadedElements[uploadedElements.length - 1]
      if (lastUploadedElement) {
        lastUploadedElement.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  async function validateFontSizesForModal() {
    // If the modal is not visible or there are no selected fonts at all, clear the lists
    if (!modalVisible) {
      resetModalVars()
      return
    }

    // Set validating state to true at the start
    validating_fonts = true

    try {
      // Use the functional validation service
      const validationResult = await validateFontSizes(
        fontsToUpload, 
        selected_fonts, 
        $dropped_font_files
      )
      
      oversized_fonts = validationResult.oversizedFonts
      valid_fonts_for_upload = validationResult.validFonts
      fonts_already_uploaded = validationResult.alreadyUploaded
    } catch (error) {
      console.error('Error validating font sizes:', error)
      // Fallback to assuming all fonts are valid
      oversized_fonts = []
      valid_fonts_for_upload = fontsToUpload
      fonts_already_uploaded = selected_fonts.filter(font => font.uploaded)
    } finally {
      // Always set validating state to false when done
      validating_fonts = false
    }
  }

  async function uploadFonts() {
    // Only process fonts that passed size validation
    if (valid_fonts_for_upload.length === 0) {
      console.log('No valid fonts to upload after size validation.');
      return; // Nothing to upload
    }

    try {
      // Use the functional service to prepare entries
      const fontEntriesToUpload = await prepareFontEntries(
        valid_fonts_for_upload,
        $dropped_font_files
      )

      if (fontEntriesToUpload.length === 0) {
        console.log('No new fonts to upload.');
        return; // Nothing to upload
      }
      
      // Call the central upload function
      const uploadResponse = await uploadAllFonts(fontEntriesToUpload);
      
      if (!uploadResponse.data.success) {
        // Consolidate errors for display or logging
        const errorMessages = uploadResponse.data.upload_errors.join('; ');
        throw new Error('Font upload failed: ' + (errorMessages || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error in uploadFonts:', error)
      throw error // Re-throw to be handled by caller
    }
  }

  async function handleSubmit(event) {
    // Track original count of fonts to upload before upload process
    const originalFontsToUploadCount = valid_fonts_for_upload.length

    // For upload mode, if no valid fonts need uploading, just close the modal
    if (currentMode === 'upload' && valid_fonts_for_upload.length === 0) {
      modals.close()
      showSuccessMessage()
      return
    }

    button_busy = true
    show_save_error = false

    try {
      // Upload logic (same for both modes)
      await uploadFonts()

      // Only save to storefront if in addToStorefront mode
      if (currentMode === 'addToStorefront') {
        console.log('ModalUploadSelectedOrAddToStorefront: handleSubmit - currentMode:', currentMode, 'fonts_valid_for_storefront before saveToStorefront:', JSON.parse(JSON.stringify(fonts_valid_for_storefront)));
        await saveToStorefront()
      }

      // Show success message and close modal
      modals.close()
      showSuccessMessage(originalFontsToUploadCount)

    } catch (error) {
      show_save_error = true
      console.error(`Error ${currentMode === 'upload' ? 'uploading fonts' : 'saving fonts to storefront'}`, error)
    } finally {
      resetModalVars()
      button_busy = false
    }
  }

  async function saveToStorefront() {
    const existing_storefront = find($user_storefronts, (s) => s.name.toLowerCase() === storefront_name.toLowerCase())
    let response;
    
    if (existing_storefront) {
      response = await saveStorefrontData({
        storefront_id: existing_storefront.id,
        fonts_to_add: fonts_valid_for_storefront,
      })
    } else {
      response = await saveStorefrontData({
        name: storefront_name,
        fonts_to_add: fonts_valid_for_storefront,
        save_view_options: true
      })
    }

    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to save storefront')
    }
  }

  function showSuccessMessage(originalFontsToUploadCount = null) {
    const fontsUploadedCount = originalFontsToUploadCount !== null ? originalFontsToUploadCount : valid_fonts_for_upload.length
    
    const successMessage = generateSuccessMessage(currentMode, {
      fontsUploadedCount,
      storefrontName: storefront_name,
      fontsValidForStorefrontCount: fonts_valid_for_storefront.length,
      userStorefronts: $user_storefronts
    })
    
    $flash_content = successMessage
  }

  function resetModalVars() {
    storefront_name = ''
    oversized_fonts = []
    valid_fonts_for_upload = []
    validating_fonts = false
  }
</script>

<!-- Single modal that changes content based on the mode store -->
<Modal id="uploadOrAddToStorefront" on:modalClosed={resetModalVars}>
  <span slot="header">
    {modalTitle}
  </span>
  <span slot="content">
    <FormError bind:visible={show_save_error}>
      {@html errorMessage}
    </FormError>
    <form bind:this={form_element} on:submit|preventDefault="{handleSubmit}">
      <div class="intro">
        {introText}
      </div>

      <!-- Font validation message -->
      {#if validating_fonts}
        <Blink>
          <p class="mb-1">{$_('modals.uploadSelected.validatingFonts')}</p>
        </Blink>
      {/if}

      <!-- Storefront selection for 'addToStorefront' mode -->
      {#if currentMode === 'addToStorefront'}
        <Fieldset>
          <SelectList {list_items} label={$_('modals.addToStorefront.storefrontLabel')} bind:entered_name={storefront_name}/>
        </Fieldset>
      {/if}
      
      <!-- Font lists section: show if fonts are selected in either 'upload' or 'addToStorefront' mode -->
      {#if selected_fonts.length > 0}
        <div class="font-lists">
          {#if (currentMode === 'upload' && !validating_fonts) || (valid_fonts_for_upload.length > 0 && currentMode === 'addToStorefront')}
            <Label>
              {$_('modals.uploadSelected.fontsToUpload')} ({valid_fonts_for_upload.length})
              {#if currentMode === 'addToStorefront'}
                <span class="will-upload-trigger">
                  {$_('buttons.learnMore')}
                </span>
                <p class="will-upload-info">{@html $_('modals.addToStorefront.fontsWillUpload')}</p>
              {/if}
            </Label>
            <div class="font-list">
              <ul bind:this={font_list_element}>
                {#each detected_fonts_validated_to_upload as font}
                  <li class:uploaded={font.uploaded}>
                    {#if font.uploaded}
                      <IconCloudDone/>
                    {/if}
                    {$detected_fonts.find(f => f.postscript_name === font.postscript_name)?.display_name || font.postscript_name}
                  </li>
                {/each}
              </ul>
            </div>
          {/if}

          <!-- Show already uploaded fonts -->
          {#if fonts_already_uploaded.length > 0 && currentMode === 'upload'}
            <p class="info">
              <b class="warning">{$_('modals.uploadSelected.fontsToSkip')} ({fonts_already_uploaded.length}):</b>
              {@html formatFontList(fonts_already_uploaded, $detected_fonts, false)}
              {#if fonts_already_uploaded.length > 1}
                <b>{@html $_('modals.uploadSelected.alreadyUploadedPlural')}</b>
              {:else}
                <b>{@html $_('modals.uploadSelected.alreadyUploadedSingular')}</b>
              {/if}
            </p>
          {/if}

          <!-- Show oversized font warnings -->
          {#if oversized_fonts.length > 0}
            <p class="info">
              <b class="danger">{$_('modals.uploadSelected.fontsWithIssues')} ({oversized_fonts.length}):</b>
              {@html formatFontList(oversized_fonts, $detected_fonts, false)}
              {#if currentMode === 'upload'}
                {@html $_('modals.uploadSelected.tooLarge', { values: { size: formatFileSize($max_upload_file_size) } })}
              {:else}
                {@html $_('modals.addToStorefront.tooLarge', { values: { size: formatFileSize($max_upload_file_size) } })}
              {/if}
            </p>
          {/if}
          
        </div>
      {/if}

      <!-- "No fonts selected" message for upload mode -->
      {#if currentMode === 'upload' && selected_fonts.length === 0}
        <div class="no-fonts">
          <p>{$_('modals.uploadSelected.noFontsSelected')}</p>
        </div>
      {/if}
      
      <ButtonGroup mt={3}>
        <Button kind="accent" type="submit" busy={button_busy}>{buttonText}</Button>
        <Button kind="outline" disabled={button_busy} on:click={() => { resetModalVars(); modals.close(); }}>{$_('buttons.cancel')}</Button>
      </ButtonGroup>
    </form>
  </span>
</Modal>

<style>
  .intro {
    margin-bottom: 2rem;
  }
  
  .font-lists {
    margin-bottom: 2rem;
  }
  
  .font-list {
    margin-bottom: 1rem;
    background-color: var(--c-content-bg);
    border-radius: 0.5rem;
    overflow: hidden;
  }
  .font-list:last-child {
    margin-bottom: 0;
  }
  
  .font-list ul {
    max-height: 112px;
    overflow-y: auto;
    padding: 0 1rem;
  }
  
  .font-list li {
    padding: 0.5rem 1rem;
    margin: 0 -1rem;
    border-bottom: 1px solid var(--c-bg-a2);
  }
  .font-list li.uploaded {
    padding-left: 2.5rem;
    color: var(--c-text-faded);
  }

  .font-list li:last-child {
    border-bottom: none;
  }
  
  .no-fonts {
    text-align: center;
    padding: 2rem;
    color: var(--c-text-faded);
  }

  .will-upload-trigger {
    float: right;
    padding: 0.2rem 0.5rem;
    color: var(--c-text-faded);
    font-size: 1.4rem;
    border-top-right-radius: 0.5rem;
    border-top-left-radius: 0.5rem;
  }
  .will-upload-trigger:hover,
  .will-upload-trigger:has(+ .will-upload-info:hover) {
    color: var(--c-warning-text);
    background: var(--c-warning-bg);
  }
  .will-upload-trigger:hover + .will-upload-info {
    display: block;
  }


  .will-upload-info {
    position: absolute;
    display: none;
    width: 100%;
    padding: 0.75rem 1rem;
    color: var(--c-warning-text);
    font-size: 1.4rem;
    line-height: 1.75rem;
    border-radius: 0.5rem;
    background: var(--c-warning-bg);
  }
  .will-upload-info :global(a) {
    color: var(--c-warning-text);
    text-decoration: underline;
  }
  .will-upload-info:hover {
    display: block;
  }

  .info {
    max-height: 10rem;
    overflow-y: auto;
    margin-bottom: 1rem;
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
    font-size: 1.4rem;
    color: var(--c-text);
    background-color: var(--c-bg-a2);
  }
  .info b.warning {
    color: var(--c-accent-orange);
  }
  .info b.danger {
    color: var(--c-danger);
  }
  .mb-1 {
    margin-bottom: 1rem;
  }
  /* when window is too short, hide some elements */
  @media (max-height: 640px) {
    .intro {
      display: none;
    }
  }
  @media (max-height: 580px) {
    .will-upload-info {
      display: none;
    }
  }
</style> 